import { useMemo } from "react";
import { canPurchaseHere } from "../../app/inAppPurchase.js";
import { useOrgCapability } from "../../app/orgContext.js";
import { usePlan } from "../../app/hooks/useBilling.js";
import { SettingsModalShell } from "../settings/SettingsModalShell.js";
import { OrgSettingsAccountTab } from "./OrgSettingsAccountTab.js";
import { OrgSettingsArtistPlanTab } from "./OrgSettingsArtistPlanTab.js";
import { OrgSettingsOrganizationTab } from "./OrgSettingsOrganizationTab.js";
import { OrgSettingsTutorialsTab } from "../settings/tutorials/SettingsTutorialsTab.js";
import { useOrgTutorials } from "../../app/hooks/useTutorials.js";
import { visibleOrgSettingsTabs, type OrgSettingsTab } from "./orgSettingsTabs.js";

export interface OrgSettingsModalProps {
  onClose: () => void;
  /** Which tab to open on. Defaults to the first tab this seat can see. */
  initialTab?: OrgSettingsTab;
  /**
   * The artist the shell currently has in focus, if any — what the "Artist plan" tab acts on (ART-142). The
   * shell owns this because the dialog mounts OUTSIDE the per-artist provider: it opens from the top bar,
   * which is above `RosterBandProvider`. Absent ⇒ no such tab, which is also the routed page's case.
   */
  artistId?: string;
}

/**
 * Org Settings as a modal — the normal way in, opened from the roster shell's account chip (PO,
 * 2026-09-01). The `/roster/settings` ROUTE survives as a full page for a bookmark, mounting these same tab
 * bodies through the same `orgSettingsTabs` list: a second chrome, never a second implementation.
 *
 * The dialog itself is `SettingsModalShell`, shared with the BAND Settings modal — same card, same tab
 * strip, same one scroll region, same "open on the first tab this seat can actually see". The two dialogs
 * differ only in which tabs they carry, and the Account tab is literally the same component in both.
 */
export function OrgSettingsModal({ onClose, initialTab, artistId }: OrgSettingsModalProps) {
  const canManageOrg = useOrgCapability("roster:manage_seats");

  // The plan read for the focused artist — disabled with no artist, and served through the org lens (the
  // client middleware rewrites the band-direct path to its /orgs/{orgId}/bands/{bandId} mirror). `action` is
  // the SERVER's verdict, folding WHO this seat is and WHICH RAIL the artist is on into one value; deriving
  // it here would be a second implementation of a rule that is both a permission and a compliance constraint.
  const plan = usePlan(artistId);
  // No purchase path inside the app (ART-346): the tab is a plan-change surface, so it is absent there.
  const canManageArtistPlan = artistId !== undefined && plan.data?.action === "change" && canPurchaseHere();

  const hasTutorials = (useOrgTutorials().data?.length ?? 0) > 0;
  const tabs = visibleOrgSettingsTabs(canManageOrg, canManageArtistPlan, hasTutorials);

  // The artist-plan body closes over the focused artist, so the shell's zero-arg body contract holds. Memoized
  // so the identity is stable across renders and the shell does not remount the panel on every keystroke
  // elsewhere in the dialog.
  const bodies = useMemo<Record<OrgSettingsTab, () => React.ReactElement>>(
    () => ({
      organization: OrgSettingsOrganizationTab,
      account: OrgSettingsAccountTab,
      artistPlan: () => <OrgSettingsArtistPlanTab artistId={artistId!} />,
      tutorials: OrgSettingsTutorialsTab,
    }),
    [artistId],
  );

  return (
    <SettingsModalShell
      tabs={tabs}
      initialTab={initialTab}
      bodies={bodies}
      tabsLabel="Settings sections"
      onClose={onClose}
    />
  );
}
